// src/services/cart-api-service.ts
// API del carrito — ordenada y compatible con el hybrid (v1 + legacy fallback)

import ApiService from "../../../utils/fetch.js";
import { toNum, r2 } from "./hybrid/cart-utils";

// ==== Bases inyectadas por Vite ====
declare const __V1_BASE__: string | undefined;
const V1_BASE =
  typeof __V1_BASE__ !== "undefined" && __V1_BASE__ ? __V1_BASE__ : "/api/v1";
const v1 = (p: string) =>
  `${V1_BASE.replace(/\/+$/, "")}/${p.replace(/^\/+/, "")}`;

/* ========================
 * Tipos request/respuesta
 * ====================== */

export interface AgregarProductoRequest {
  cod_drog: string;
  cantidad: string | number;  // ✅ Aceptar ambos
  tipo_solicitante?: "C" | "V" | "O" | "T" | "P";
  observacion?: string;
  forzar_sin_combo?: boolean;
}

export interface ValidarOfertasRequest {
  cod_drog: string;
  cantidad: number;
}

export interface ValidarOfertasResponse {
  success: boolean;
  data?: {
    producto: { cod_drog: string; descripcion?: string; stock?: "S" | "N" };
    cantidad_solicitada: number;
    descuentos_ofertas?: {
      tiene_oferta_cantidad?: boolean;
      cumple_oferta?: boolean;
      precio_normal?: number;
      precio_oferta?: number;
      cantidad_minima_oferta?: number;
      ahorro_total?: number;
    };
    pertenece_combos?: boolean;
    combos_disponibles?: Array<{
      cod_combo: string; nombre?: string; precio_combo?: number; ahorro?: number;
    }>;
    recomendaciones?: Array<any>;
  };
  message?: string;
}

export interface AgregarComboRequest {
  cod_combo: number | string;
  observacion?: string;
  tipo_solicitante?: "C" | "V" | "O";
  selecciones?: Array<{
    codigo_sel: string;
    codigo_drog?: string;
    cod_drog?: string;
    cantidad?: number | string;
  }>;
  cantidad?: number;
}

/** 👉 Es clave que `cod_combo` sea string y que `tipo_solicitante` sea "C" | "V" | "O". */
export interface AgregarComboOfertaRequest {
  cod_combo: string;
  tipo_solicitante?: "C" | "V" | "O";  // ✅ Agregado "O"
  cantidad?: number;
  observacion?: string | null;
  selecciones?: Array<{
    codigo_sel: string;
    codigo_drog: string;
    cantidad?: number;  // 👈 Agregar cantidad opcional
  }>;
}

export interface ActualizarCantidadRequest {
  cantidad: string;
  tipo_solicitante?: "C" | "V" | "O" | "T" | "P";
}

export interface EnviarPedidoItem {
  id?: string | number;
  cod_drog: string;
  cantidad: number | string;
  precio_unitario?: number | string;
  precio_total?: number | string;
  es_combo?: boolean;
  combo_id?: string | number | null;
}
export interface EnviarPedidoRequest {
  tipo_envio: "envio" | "retiro";
  items: EnviarPedidoItem[];
}
export interface EnviarPedidoRequestV1 {
  idtipo_pedido: number;
  items: Array<{ cod_drog: string; cantidad: number | string }>;
  notas?: string;
}
export interface EnviarPedidoResponse {
  numero?: string | number;
  id?: string | number;
  [k: string]: any;
}

export interface ProductoCarritoAPI {
  id_pedido: string | number;
  id?: string | number;
  cod_drog: string;
  descripcion: string;
  cantidad: number;
  precio_unitario: number;
  precio_total: number;
  tipo_solicitante: string;
  observacion?: string;
  es_combo: boolean;
  combo_id?: string | number;
  combo?: string | number | boolean;
  imagen?: string;
  descuento?: number;
  stock?: string;
  laboratorio?: string;
  rubro?: string;
  marca?: string;
  titulo?: string;
  expendedor?: string;
  oferta?: string;
  refrigerado?: string;
  transfer?: string;
  fora?: string;
  dto_general?: number | string;
  iva?: number | string;
  unidxbulto?: number | string;
  idimagen?: string;
  cod_barra?: string;
  pr_farmacia_si?: number;
  pr_farmacia_con_descuento?: number;
  pr_publico?: number;
  precio_lista?: number;
  precio_unitario_original?: number;
  is_oferta_online?: boolean;
  scope?: "carrito" | "mis-pedidos";
  raw_combo?: any;
  monto_total?: number;
  cantidad_combos?: number;
  fecha_carga?: string;
  precio?: number;
  precio_si?: number;
  precio_oferta?: number;
  desc_porc?: number;
  // ✅ Campos críticos para mostrar en tabla de carrito
  precio_unitario_sin_iva?: number;
  importe_sin_iva?: number;
  importe_con_iva?: number;
  // Auditoría: quién cargó el producto
  usuario_carga?: string;
  nombre_carga?: string;
}

export interface CarritoResponse {
  success: boolean;
  data: {
    productos: ProductoCarritoAPI[];
    medicamentos?: ProductoCarritoAPI[];
    perfumeria?: ProductoCarritoAPI[];
    accesorios?: ProductoCarritoAPI[];
    otros?: ProductoCarritoAPI[];
    subtotal: number;
    descuentos: number;
    total: number;
    cantidad_items: number;
    ofertas_online?: ProductoCarritoAPI[];
    total_ofertas_online?: number;
    cantidad_ofertas_online?: number;
    pedidos_individuales?: any[];
    pedidos_combos?: any[];
    pedidos_combos_oferta?: any[];
  };
  message?: string;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  code?: number | string;
}

interface ApiServiceResponse {
  success?: boolean;
  code?: number | string;
  message?: string;
  data?: any;
  unauthorized?: boolean;
  [key: string]: any;
}

/* ================ Implementación =============== */
class CartApiService {
  // eventThrottle removido (siempre fresco)

  /* ------------ v1 helpers (usa cookie/localStorage auth_token) ------------ */
  private async postV1<T = any>(path: string, body: any): Promise<T> {
    const url = v1(path);
    const token = localStorage.getItem("auth_token") || localStorage.getItem("token");

    const headers: HeadersInit = {
      "Content-Type": "application/json",
      Accept: "application/json"
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const res = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers,
      body: JSON.stringify(body ?? {}),
    });

    const ct = res.headers.get("content-type") || "";
    const json = ct.includes("application/json") ? await res.json() : {};
    return Object.assign(json || {}, { __http: res.status });
  }

  private async putV1<T = any>(path: string, body: any): Promise<T> {
    const url = v1(path);
    const token = localStorage.getItem("auth_token") || localStorage.getItem("token");

    const headers: HeadersInit = {
      "Content-Type": "application/json",
      Accept: "application/json"
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const res = await fetch(url, {
      method: "PUT",
      credentials: "include",
      headers,
      body: JSON.stringify(body ?? {}),
    });

    const ct = res.headers.get("content-type") || "";
    const json = ct.includes("application/json") ? await res.json() : {};
    return Object.assign(json || {}, { __http: res.status });
  }

  private async getV1<T = any>(path: string): Promise<T> {
    const url = v1(path);
    const token = localStorage.getItem("auth_token") || localStorage.getItem("token");

    const headers: HeadersInit = {
      Accept: "application/json"
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const res = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers,
    });

    const ct = res.headers.get("content-type") || "";
    const json = ct.includes("application/json") ? await res.json() : {};
    if (!res.ok) throw new Error((json as any)?.message || `HTTP ${res.status}`);
    return json as T;
  }

  /* ------------ Fallbacks HTTP ------------ */
  private async postWithFallback(path: string, body: any): Promise<ApiServiceResponse> {
    try {
      const respV1 = (await this.postV1(path, body)) as ApiServiceResponse;

      if (respV1?.success === true || respV1?.data) return respV1;

      if (respV1?.code === 401 || respV1?.code === 403 ||
        (respV1 as any).__http === 401 || (respV1 as any).__http === 403) {
        return Object.assign({}, respV1, { unauthorized: true });
      }

      throw new Error(respV1?.message || 'Error en v1');

    } catch (error) {
      throw error;
    }
  }

  private async getWithFallback(path: string): Promise<ApiServiceResponse> {
    try {
      const v1json = await this.getV1<ApiServiceResponse>(path);
      if (v1json) return v1json;
    } catch { }
    const r = (await ApiService.get(path)) as ApiServiceResponse;
    if ((r?.code === 404 || r?.code === "404") && !path.startsWith("api-web/")) {
      return (await ApiService.get(`api-web/${path}`)) as ApiServiceResponse;
    }
    return r;
  }

  private async putWithFallback(path: string, body: any): Promise<ApiServiceResponse> {
    try {
      const v1json = await this.putV1<ApiServiceResponse>(path, body);
      if (v1json) return v1json;
    } catch { }
    const r = (await ApiService.put(path, body)) as ApiServiceResponse;
    if ((r?.code === 404 || r?.code === "404") && !path.startsWith("api-web/")) {
      return (await ApiService.put(`api-web/${path}`, body)) as ApiServiceResponse;
    }
    return r;
  }

  private async deleteWithFallback(path: string): Promise<ApiServiceResponse> {
    const r = (await ApiService.delete(path)) as ApiServiceResponse;
    if ((r?.code === 404 || r?.code === "404") && !path.startsWith("api-web/")) {
      return (await ApiService.delete(`api-web/${path}`)) as ApiServiceResponse;
    }
    return r;
  }

  private async deleteV1WithBody<T = any>(path: string, body: any): Promise<T> {
    const url = v1(path);
    const token = localStorage.getItem("auth_token") || localStorage.getItem("token");
    const headers: HeadersInit = {
      "Content-Type": "application/json",
      Accept: "application/json"
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    const res = await fetch(url, {
      method: "DELETE",
      credentials: "include",
      headers,
      body: JSON.stringify(body ?? {}),
    });
    const ct = res.headers.get("content-type") || "";
    const json = ct.includes("application/json") ? await res.json() : {};
    return Object.assign(json || {}, { __http: res.status });
  }

  /* ------------ Eventos (throttle + broadcast) ------------ */
  private dispatchCartEvent(source: string, delay = 200): void {
    // Throttle removido
    setTimeout(async () => {
      try {
        const carrito = await this.obtenerCarrito();
        if (carrito.success) {
          window.dispatchEvent(
            new CustomEvent("cart-updated", {
              detail: { source, carritoData: carrito.data, timestamp: Date.now() },
              bubbles: true,
            })
          );
          window.dispatchEvent(
            new CustomEvent("cart-data-loaded", {
              detail: { carritoData: carrito.data, source, timestamp: Date.now() },
              bubbles: true,
            })
          );
        }
      } catch (err) {
        console.error(`💥 [API] Error disparando evento ${source}:`, err);
      }
    }, delay);
  }


  async validarOfertas(payload: ValidarOfertasRequest): Promise<ValidarOfertasResponse> {
    try {
      const resp = await this.postV1<ValidarOfertasResponse>("carrito/validar-oferta", {
        cod_drog: String(payload.cod_drog).trim(),
        cantidad: Number(payload.cantidad) || 1,
      });

      const ok = (resp as any)?.success === true;
      return ok ? resp : { success: false, data: resp?.data, message: (resp as any)?.message || "No se pudo validar." };
    } catch (e) {
      return { success: false, message: this.getErrorMessage(e) };
    }
  }

  /* ======= 🚀 BATCH: Validar múltiples ofertas EN UN REQUEST ======= */
  async validarOfertasEnLote(
    productos: Array<{ cod_drog: string; cantidad: number }>
  ): Promise<{ success: boolean; data?: any; message?: string }> {
    try {
      if (!productos || productos.length === 0) {
        return { success: false, message: "No hay productos para validar" };
      }

      const payload = {
        productos: productos.map((p) => ({
          cod_drog: String(p.cod_drog).trim(),
          cantidad: Number(p.cantidad) || 1,
        })),
      };

      const resp = await this.postV1<any>("carrito/validar-oferta", payload);

      const ok = (resp as any)?.success === true;
      if (!ok) {
        return {
          success: false,
          data: resp?.data,
          message: (resp as any)?.message || "No se pudieron validar las ofertas",
        };
      }

      return {
        success: true,
        data: resp?.data,
        message: resp?.message,
      };
    } catch (e) {
      return { success: false, message: this.getErrorMessage(e) };
    }
  }
  /* ------------ Endpoints ------------ */
  async agregarProducto(req: AgregarProductoRequest): Promise<ApiResponse<any>> {
    try {
      const payload = {
        cod_drog: req.cod_drog,
        cantidad: Number(req.cantidad),
        tipo_solicitante: req.tipo_solicitante || "C",
        ...(req.observacion ? { observacion: req.observacion } : {}),
        ...(req.forzar_sin_combo !== undefined ? { forzar_sin_combo: req.forzar_sin_combo } : {})
      };

      // ✅ VALIDACIÓN PREVENTIVA: Verificar si la cantidad cumple con requisitos de oferta/múltiplo
      try {
        console.log(`🔍 [agregarProducto] Iniciando validación preventiva para ${payload.cod_drog}, cantidad: ${payload.cantidad}`);

        const validacion = await this.validarOfertas({
          cod_drog: payload.cod_drog,
          cantidad: payload.cantidad
        });

        console.log(`📊 [agregarProducto] Validación de ofertas devolvió:`, validacion);

        if (validacion?.success && validacion?.data) {
          const descuentos = validacion.data.descuentos_ofertas;

          // Si hay una oferta de cantidad y la cantidad NO es múltiplo válido
          if (descuentos?.tiene_oferta_cantidad && descuentos?.cantidad_minima_oferta) {
            const cantMin = Number(descuentos.cantidad_minima_oferta);
            const esMultiplo = payload.cantidad % cantMin === 0;
            const esClienteOferta = payload.tipo_solicitante === 'O';

            console.log(`🔢 [agregarProducto] Análisis: cantMin=${cantMin}, cantidad=${payload.cantidad}, esMultiplo=${esMultiplo}, tipo=${payload.tipo_solicitante}`);

            // ✅ PERMITIR cualquier cantidad si es CLIENTE COMÚN ('C')
            // ❌ SOLO BLOQUEAR si es CLIENTE DE OFERTA ('O') y no es múltiplo
            if (!esMultiplo && esClienteOferta) {
              // ❌ RECHAZAR: Cantidad no es múltiplo válido SOLO para clientes de oferta
              const proximoValido = Math.ceil(payload.cantidad / cantMin) * cantMin;
              console.log(`❌ [agregarProducto] BLOQUEANDO (Cliente Oferta): Cantidad no es múltiplo. Próximo válido: ${proximoValido}`);

              return {
                success: false,
                message: `La cantidad debe ser múltiplo de ${cantMin}. Próxima válida: ${proximoValido} unidades.`,
                code: 422,
                data: {
                  cantidad_actual: payload.cantidad,
                  cantidad_minima_multiplo: cantMin,
                  cantidad_proxima_valida: proximoValido
                }
              };
            } else if (!esMultiplo) {
              console.log(`✅ [agregarProducto] PERMITIENDO (Cliente Común): Cantidad ${payload.cantidad} no es múltiplo de ${cantMin}, pero es cliente común (tipo='C')`);
            }
          }
        }
      } catch (validationError) {
        // Si hay error en validación, continuar igualmente (no es bloqueante)
        console.warn("⚠️ [agregarProducto] Error en validación preventiva:", validationError);
      }

      console.log(`📤 [agregarProducto] Enviando al API: cod_drog=${payload.cod_drog}, cantidad=${payload.cantidad}, tipo=${payload.tipo_solicitante}`);
      const resp = await this.postWithFallback("carrito/items", payload);
      console.log(resp);

      const httpStatus = (resp as any).__http;

      if (httpStatus === 422) {
        return {
          success: false,
          message: resp.message || "Los datos proporcionados no son válidos",
          code: 422,
          data: resp?.data || (resp as any).errors
        };
      }

      if (resp?.code === 409 || resp?.code === "PERTENECE_COMBO") {
        return {
          success: false,
          message: resp.message || "El producto pertenece a uno o más combos",
          data: resp.data,
          code: 409,
        };
      }

      if (resp?.unauthorized || httpStatus === 401 || httpStatus === 403) {
        return {
          success: false,
          message: "No tienes permisos para realizar esta acción",
          code: httpStatus || 403
        };
      }

      if (resp?.success === false) {
        // ✅ Preservar mensaje específico del servidor (puede tener detalles sobre validación, múltiplos, etc.)
        console.log('❌ [agregarProducto] Respuesta con success=false:', resp);

        // ✅ DETECCIÓN DE ERROR DE MÚLTIPLO en el mensaje
        const mensaje = resp.message || "No se pudo agregar el producto";
        const tieneMensajMultiplo = mensaje.toLowerCase().includes("múltiplo");

        let code = resp?.code;
        let data = resp?.data;

        if (tieneMensajMultiplo) {
          // Es un error de múltiplo - asegurar que tenga código y datos
          if (!code) {
            code = 422;
          }

          // Si no tiene datos, extraer del mensaje
          if (!data || !data.cantidad_minima_multiplo) {
            const matchMin = mensaje.match(/múltiplo de (\d+)/i);
            const matchProximo = mensaje.match(/próxima[^:]*:\s*(\d+)/i);
            const cantMin = matchMin ? Number(matchMin[1]) : 3;
            const proximoValido = matchProximo ? Number(matchProximo[1]) : cantMin * 2;

            data = {
              cantidad_actual: payload.cantidad,
              cantidad_minima_multiplo: cantMin,
              cantidad_proxima_valida: proximoValido
            };

            console.log('🔍 Error de múltiplo detectado en mensaje:', { cantMin, proximoValido });
          } else {
            console.log('🔍 Error de múltiplo con datos completos:', data);
          }
        }

        return {
          success: false,
          message: mensaje,
          code: code,
          data: data
        };
      }

      this.dispatchCartEvent("api-add-product");

      // ✅ CRÍTICO: Si se agregó como tipo_solicitante = "C" (Mantener/Usuario rechazó oferta)
      // SIEMPRE limpiar descuentos y campos de oferta para que NO se muestre como oferta en carrito
      const productoAgregado = resp.data;

      if (productoAgregado && payload.tipo_solicitante === "C") {
        // El usuario eligió "Mantener" = rechazó la oferta
        // Debe guardarse SIEMPRE como producto normal, sin ningún descuento
        if (productoAgregado.pedido) {
          productoAgregado.pedido.desc_porc = 0;
          productoAgregado.pedido.descuento = 0;
          productoAgregado.pedido.tipo_solicitante = "C";
        }
        if (productoAgregado.descuentos) {
          productoAgregado.descuentos.descuento_porcentaje = 0;
          productoAgregado.descuentos.ahorro_unitario = 0;
          productoAgregado.descuentos.ahorro_total = 0;
          productoAgregado.descuentos.cumple_oferta = false;
        }
        // Marcar que el producto fue agregado sin oferta (para poder ofrecerla después en carrito)
        (productoAgregado as any).__mantener = true;
      }

      return {
        success: true,
        data: productoAgregado,
        message: resp.message || "Producto agregado correctamente",
      };

    } catch (error) {
      if ((error as any)?.message?.includes("Failed to fetch")) {
        return {
          success: false,
          message: "Error de conexión. Verifica tu conexión a internet."
        };
      }

      return {
        success: false,
        message: this.getErrorMessage(error)
      };
    }
  }

  /* ======= 🚀 NUEVO: Validar y agregar productos como ARRAY ======= */
  /**
   * ✨ ESTRATEGIA HÍBRIDA:
   * PASO 1: Validar TODAS las ofertas EN 1 REQUEST (validarOfertasEnLote)
   * PASO 2: Agregar EN PARALELO usando endpoint individual (optimizado sin validar de nuevo)
   * Beneficio: Menos requests que N*2, y compatible con backend actual
   */
  async agregarProductosEnLoteAPI(
    productos: Array<{
      cod_drog: string;
      cantidad: number | string;
      tipo_solicitante?: "C" | "V" | "O" | "T" | "P";
      observacion?: string;
      forzar_sin_combo?: boolean;
    }>
  ): Promise<ApiResponse<any>> {
    try {
      if (!productos || productos.length === 0) {
        return { success: false, message: "No hay productos para agregar" };
      }

      console.log(`🚀 [agregarProductosEnLoteAPI] Iniciando batch: ${productos.length} productos`);

      const resultadoLote = await this.agregarProductosEnLoteBatch(productos);

      if (!resultadoLote.success) {
        console.error(`❌ [agregarProductosEnLoteAPI] Error en batch:`, resultadoLote.message);
        return resultadoLote;
      }

      const exitosos = resultadoLote.data?.exitosos || resultadoLote.data?.total_exitosos || 0;
      const errores = resultadoLote.data?.errores || [];
      const productosAgregados = resultadoLote.data?.productos || [];

      console.log(`✅ [agregarProductosEnLoteAPI] PASO 2 completado: ${exitosos} exitosos, ${errores.length} errores`);

      return {
        success: exitosos > 0,
        data: {
          exitosos,
          errores,
          productos: productosAgregados,
        },
        message: `${exitosos} productos agregados, ${errores.length} errores`,
      };
    } catch (error) {
      console.error(`❌ [agregarProductosEnLoteAPI] Error:`, error);

      if ((error as any)?.message?.includes("Failed to fetch")) {
        return {
          success: false,
          message: "Error de conexión. Verifica tu conexión a internet.",
        };
      }

      return {
        success: false,
        message: this.getErrorMessage(error),
      };
    }
  }

  /* ======= 🚀 BATCH: Agregar múltiples productos EN UN REQUEST ======= */
  /**
   * ✨ Envía array de productos al endpoint de agregar batch
   * Ventaja: 1 request en lugar de N requests paralelos
   * Request: { "productos": [...], "tipo_solicitante": "C", "forzar_sin_combo": false }
   * Response: { "success": true, "data": { "resultados": [...], "total_procesados": 3, "total_exitosos": 2, "total_errores": 1 } }
   */
  async agregarProductosEnLoteBatch(
    productos: Array<{
      cod_drog: string;
      cantidad: number | string;
      tipo_solicitante?: "C" | "V" | "O" | "T" | "P";
      observacion?: string;
      forzar_sin_combo?: boolean;
    }>
  ): Promise<ApiResponse<any>> {
    try {
      if (!productos || productos.length === 0) {
        return { success: false, message: "No hay productos para agregar" };
      }

      console.log(`🚀 [agregarProductosEnLoteBatch] Agregando ${productos.length} productos EN UN REQUEST...`);

      // Agrupar por tipo_solicitante para enviar cada grupo con su tipo correcto
      const grupos = new Map<string, typeof productos>();
      for (const p of productos) {
        const tipo = p.tipo_solicitante || "C";
        if (!grupos.has(tipo)) grupos.set(tipo, []);
        grupos.get(tipo)!.push(p);
      }

      let totalExitosos = 0;
      let totalErrores = 0;
      let totalProcesados = 0;
      const todosErrores: any[] = [];
      const todosProductos: any[] = [];

      for (const [tipo, grupo] of grupos) {
        const payload = {
          productos: grupo.map((p) => ({
            cod_drog: String(p.cod_drog).trim(),
            cantidad: Number(p.cantidad) || 1,
            ...(p.observacion ? { observacion: p.observacion } : {}),
          })),
          tipo_solicitante: tipo,
          forzar_sin_combo: grupo[0]?.forzar_sin_combo || false,
        };

        console.log(`📤 [agregarProductosEnLoteBatch] Enviando grupo tipo=${tipo} (${grupo.length} productos):`, payload.productos);

        const resp = await this.postWithFallback("carrito/items", payload);
        const httpStatus = (resp as any)?.__http;

        if (httpStatus === 422 || !resp?.success) {
          return {
            success: false,
            message: resp?.message || "Error agregando productos",
            code: 422,
            data: resp?.data || (resp as any).errors,
          };
        }

        if (resp?.unauthorized || httpStatus === 401 || httpStatus === 403) {
          return {
            success: false,
            message: "No tienes permisos para realizar esta acción",
            code: httpStatus || 403,
          };
        }

        totalProcesados += resp?.data?.total_procesados || resp?.data?.resultados?.length || 0;
        totalExitosos += resp?.data?.total_exitosos || 0;
        totalErrores += resp?.data?.total_errores || 0;
        if (resp?.data?.errores) todosErrores.push(...resp.data.errores);
        if (resp?.data?.resultados) todosProductos.push(...resp.data.resultados);
      }

      console.log(`✅ [agregarProductosEnLoteBatch] Completado: ${totalExitosos} exitosos, ${totalErrores} errores`);

      return {
        success: true,
        data: {
          exitosos: totalExitosos,
          errores: todosErrores,
          productos: todosProductos,
          total_procesados: totalProcesados,
          total_exitosos: totalExitosos,
          total_errores: totalErrores,
        },
        message: `${totalExitosos} productos agregados, ${totalErrores} errores`,
      };
    } catch (error) {
      console.error(`❌ [agregarProductosEnLoteBatch] Error:`, error);

      if ((error as any)?.message?.includes("Failed to fetch")) {
        return {
          success: false,
          message: "Error de conexión. Verifica tu conexión a internet.",
        };
      }

      return {
        success: false,
        message: this.getErrorMessage(error),
      };
    }
  }

  async agregarCombo(req: AgregarComboRequest): Promise<ApiResponse<any>> {
    try {
      // Validar que cod_combo existe
      const cod_combo = String(req.cod_combo ?? "").trim();
      if (!cod_combo) {
        return { success: false, message: `Código de combo inválido: ${req.cod_combo}` };
      }

      const payload: any = {
        cod_combo: cod_combo,
        tipo_solicitante: req.tipo_solicitante || "C",
        cantidad: req.cantidad ? Math.max(1, Number(req.cantidad)) : 1,
        ...(req.observacion ? { observacion: req.observacion } : {}),
        selecciones: req.selecciones ?? [],
      };

      const r = await this.postWithFallback("carrito/combos", payload);
      if (!r?.success) {
        return { success: false, message: r?.message || "Error agregando combo", data: r?.data, code: r?.code };
      }
      this.dispatchCartEvent("api-add-combo");
      return { success: true, data: r.data, message: r.message || "Combo agregado correctamente" };
    } catch (error) {
      return { success: false, message: this.getErrorMessage(error) };
    }
  }

  /** ✅ Implementación corregida de agregarComboOferta */
  async agregarComboOferta(req: AgregarComboOfertaRequest): Promise<ApiResponse<any>> {
    try {
      const cod_combo = String(req.cod_combo ?? "").trim();
      if (!cod_combo) return { success: false, message: "cod_combo requerido" };

      const tipo = (req.tipo_solicitante ?? "C").toUpperCase() as "C" | "V" | "O";

      const selecciones =
        (req.selecciones ?? [])
          .map((s) => {
            const codigo_sel = String(s?.codigo_sel ?? "").trim();
            const codigo_drog = String((s as any)?.codigo_drog ?? (s as any)?.cod_drog ?? "").trim();
            const cantidad = Number(s?.cantidad || 1);

            if (!codigo_sel || !codigo_drog) return null;

            return {
              codigo_sel,
              codigo_drog,
              cantidad
            };
          })
          .filter(Boolean) as Array<{
            codigo_sel: string;
            codigo_drog: string;
            cantidad: number;
          }>;

      const payload: any = {
        cod_combo,
        tipo_solicitante: tipo,
      };

      // ✅ CRÍTICO: Solo enviar cantidad si es > 1 o si no es tipo D
      if (tipo !== "O" && Number.isFinite(req.cantidad as any) && (req.cantidad as any) > 0) {
        payload.cantidad = Number(req.cantidad);
      }

      if (req.observacion != null && String(req.observacion).length) {
        payload.observacion = req.observacion;
      }

      if (selecciones.length > 0) {
        payload.selecciones = selecciones;
      }

      console.log("🔍 [agregarComboOferta] Payload FINAL enviado:", JSON.stringify(payload, null, 2));

      const resp = await this.postWithFallback("ofertas-online/carrito/combos", payload);

      console.log("📦 [agregarComboOferta] Respuesta del servidor:", JSON.stringify(resp, null, 2));

      if (resp?.unauthorized || resp?.code === 401 || resp?.code === 403) {
        return { success: false, message: "Sesión expirada. Por favor, vuelve a iniciar sesión.", code: 401 };
      }

      if (!resp?.success) {
        console.error("❌ [agregarComboOferta] Error del servidor:", resp);
        return {
          success: false,
          message: resp?.message || "Error agregando combo de oferta",
          data: resp?.data,
          code: resp?.code
        };
      }

      this.dispatchCartEvent("api-add-combo-oferta");
      return { success: true, data: resp.data, message: resp.message || "Combo oferta agregado al carrito" };
    } catch (error) {
      console.error("💥 [agregarComboOferta] Excepción capturada:", error);
      return { success: false, message: this.getErrorMessage(error) };
    }
  }

  // ----- Carrito -----
  async obtenerCarrito(): Promise<CarritoResponse> {
    // 🛑 GUARDA: Si estamos en admin, no permitir el fetch al carrito de CLIENTE
    if (typeof window !== 'undefined' && (window.location.hash.includes("/admin") || window.location.pathname.includes("/admin"))) {
      // console.log("[CartApi] Admin view detected. Returning empty cart.");
      const emptyData = {
        productos: [],
        medicamentos: [],
        perfumeria: [],
        accesorios: [],
        otros: [],
        subtotal: 0,
        descuentos: 0,
        total: 0,
        cantidad_items: 0,
        ofertas_online: [],
        total_ofertas_online: 0,
        cantidad_ofertas_online: 0,
        pedidos_individuales: [],
        pedidos_combos: [],
        pedidos_combos_oferta: [],
      };
      return { success: true, data: emptyData as any };
    }

    try {
      const response = await this.getWithFallback("carrito");
      if (!response) throw new Error("Respuesta vacía del servidor");

      const { lineasCarrito, ofertasOnline } = this.procesarRespuestaCarrito(response);

      const medicamentos = lineasCarrito.filter((p) => this.esCategoria(p, "medicamentos"));
      const perfumeria = lineasCarrito.filter((p) => this.esCategoria(p, "perfumeria"));
      const accesorios = lineasCarrito.filter((p) => this.esCategoria(p, "accesorios"));
      const otros = lineasCarrito.filter(
        (p) => !this.esCategoria(p, "medicamentos") && !this.esCategoria(p, "perfumeria") && !this.esCategoria(p, "accesorios")
      );

      const subtotal = this.calcularSubtotal(lineasCarrito);
      const descuentos = this.calcularDescuentos(lineasCarrito);
      const total = Math.max(0, Number(subtotal) - Number(descuentos));
      const cantidad_items = this.calcularCantidadItems(lineasCarrito);

      const total_ofertas_online = this.calcularSubtotal(ofertasOnline);
      const cantidad_ofertas_online = ofertasOnline.length;

      const data = {
        productos: lineasCarrito,
        medicamentos,
        perfumeria,
        accesorios,
        otros,
        subtotal: Number(subtotal) || 0,
        descuentos: Number(descuentos) || 0,
        total: Number(total) || 0,
        cantidad_items: Number(cantidad_items) || 0,
        ofertas_online: ofertasOnline,
        total_ofertas_online: Number(total_ofertas_online) || 0,
        cantidad_ofertas_online: Number(cantidad_ofertas_online) || 0,
        pedidos_individuales: response?.data?.pedidos_individuales || [],
        pedidos_combos: response?.data?.pedidos_combos || [],
        pedidos_combos_oferta: response?.data?.pedidos_combos_oferta || [],
      };

      return { success: true, data };
    } catch (error: unknown) {
      const base = {
        productos: [] as ProductoCarritoAPI[],
        subtotal: 0,
        descuentos: 0,
        total: 0,
        cantidad_items: 0,
        ofertas_online: [] as ProductoCarritoAPI[],
        total_ofertas_online: 0,
        cantidad_ofertas_online: 0,
        pedidos_individuales: [],
        pedidos_combos: [],
        pedidos_combos_oferta: [],
      };
      const err: any = error;
      if (err?.code === "NETWORK_ERROR" || !navigator.onLine) {
        return { success: false, data: base, message: "Sin conexión a internet. Usando datos locales." };
      }
      return { success: false, data: base, message: this.getErrorMessage(error) };
    }
  }

  async actualizarCantidad(idPedido: string | number, nuevaCantidad: number, tipo_solicitante?: string): Promise<ApiResponse> {
    if (!idPedido) return { success: false, message: "ID de pedido requerido" };
    if (nuevaCantidad < 1) return { success: false, message: "La cantidad debe ser mayor a 0" };
    if (nuevaCantidad > 999) return { success: false, message: "La cantidad no puede exceder 999 unidades" };

    try {
      const request: ActualizarCantidadRequest = { cantidad: String(nuevaCantidad) };
      if (tipo_solicitante) request.tipo_solicitante = tipo_solicitante as any;

      const response = await this.putWithFallback(`carrito/items/${idPedido}`, request);
      if (!response?.success) {
        return { success: false, message: response?.message || "Error actualizando cantidad en el servidor" };
      }
      return { success: true, data: response.data, message: response.message || "Cantidad actualizada correctamente" };
    } catch (error: unknown) {
      const e: any = error;
      if (e?.response?.status === 422) return { success: false, message: "Datos de cantidad no válidos." };
      if (e?.response?.status === 404) return { success: false, message: "Producto no encontrado en el carrito" };
      if (e?.response?.status === 409) return { success: false, message: "No se puede modificar la cantidad de un combo" };
      return { success: false, message: this.getErrorMessage(error) };
    }
  }

  async eliminarPorCodDrog(cod_drog: string): Promise<{ success: boolean; message?: string }> {
    try {
      const cod = String(cod_drog).trim();
      if (!cod) return { success: false, message: "Código inválido" };

      const carrito = await this.obtenerCarrito();
      const rows: any[] = Array.isArray(carrito?.data?.pedidos_individuales)
        ? carrito.data.pedidos_individuales
        : [];

      const ids = rows
        .filter((p) => String(p?.cod_drog).trim() === cod && !p?.es_combo)
        .map((p) => p?.id_pedido)
        .filter(Boolean);

      if (ids.length === 0) return { success: true, message: "SKU no presente en servidor" };

      const results = await Promise.allSettled(ids.map((id) => this.deleteWithFallback(`carrito/items/${id}`)));
      const fallos =
        results.some(
          (r: any) => r.status === "rejected" || (r.status === "fulfilled" && !r.value?.success)
        ) || false;

      return fallos
        ? { success: false, message: "Algunas líneas no pudieron eliminarse" }
        : { success: true, message: "Producto eliminado" };
    } catch {
      return { success: false, message: "Error eliminando producto" };
    }
  }

  async eliminarProducto(id: string | number): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await this.deleteWithFallback(`carrito/items/${id}`);
      return { success: Boolean(response?.success), message: response?.message || "Producto eliminado" };
    } catch {
      return { success: false, message: "Error de conexión eliminando producto" };
    }
  }

  async eliminarCombo(comboId: string | number): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await this.deleteWithFallback(`carrito/combos/${String(comboId)}`);
      return { success: Boolean(response?.success), message: response?.message || "Combo eliminado" };
    } catch {
      return { success: false, message: "Error de conexión eliminando combo" };
    }
  }

  // ✅ NUEVO: Eliminar múltiples productos en UN SOLO REQUEST (Batch)
  // Endpoint: POST /api/v1/carrito/eliminarVarios
  async eliminarVariosProductos(ids: Array<string | number>): Promise<{
    success: boolean;
    message: string;
    data?: {
      resultados: Array<{
        id: string | number;
        success: boolean;
        message?: string;
        error?: string;
        error_code?: string;
        combo_eliminado?: number;
      }>;
      total_procesados: number;
      total_exitosos: number;
      total_errores: number;
    };
  }> {
    try {
      if (!ids || ids.length === 0) {
        return { success: false, message: "No hay productos para eliminar" };
      }

      console.log(`⚡ [BATCH DELETE] Iniciando eliminación en lote de ${ids.length} producto(s) EN UN SOLO REQUEST`);
      const inicio = performance.now();

      // Convertir IDs a números si es posible para consistencia
      const idsProcessados = ids.map(id => Number(id) || id);

      // DELETE /api/v1/carrito/items con body { ids: [...] }
      const response = await this.deleteV1WithBody("carrito/items", {
        ids: idsProcessados,
      }) as any;

      const duracion = (performance.now() - inicio).toFixed(0);

      if (response?.success) {
        const data = response.data || {};
        console.log(
          `✅ [BATCH DELETE] Completado en ${duracion}ms: ${data.total_exitosos || 0}/${data.total_procesados || 0} exitosos`
        );

        return {
          success: true,
          message: response.message || "Productos eliminados correctamente",
          data,
        };
      } else {
        console.error(`❌ [BATCH DELETE] Error:`, response?.message);
        return {
          success: false,
          message: response?.message || "Error eliminando productos",
        };
      }
    } catch (e: any) {
      console.error(`❌ [BATCH DELETE] Error de conexión:`, e?.message);
      return {
        success: false,
        message: this.getErrorMessage(e) || "Error eliminando productos",
      };
    }
  }

  async limpiarCarrito(): Promise<ApiResponse> {
    try {
      const resp = await this.deleteWithFallback("carrito");
      return { success: Boolean(resp?.success ?? true), message: resp?.message || "Carrito limpiado" };
    } catch (e) {
      return { success: false, message: this.getErrorMessage(e) };
    }
  }

  // ✅ NUEVO: Vaciar carrito directamente con DELETE /api/v1/carrito/
  async vaciarCarrito(): Promise<ApiResponse> {
    try {
      const resp = await this.deleteWithFallback("carrito");
      return { success: Boolean(resp?.success ?? true), message: resp?.message || "Carrito vaciado" };
    } catch (e) {
      return { success: false, message: this.getErrorMessage(e) };
    }
  }

  async sincronizarCarrito(): Promise<CarritoResponse> {
    return this.obtenerCarrito();
  }

  // ----- v1 -----
  private mapTipoEnvioATipoPedido(t: "envio" | "retiro"): number {
    return t === "retiro" ? 2 : 1;
  }

  async enviarPedidoV1(payload: EnviarPedidoRequestV1): Promise<ApiResponse<EnviarPedidoResponse>> {
    try {
      const url = v1("pedidos");
      const res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({
          idtipo_pedido: Number(payload.idtipo_pedido),
          items: payload.items.map((it) => ({ cod_drog: String(it.cod_drog), cantidad: String(it.cantidad) })),
          ...(payload.notas ? { notas: String(payload.notas) } : {}),
        }),
      });

      const ctype = res.headers.get("content-type") || "";
      if (!res.ok) {
        const txt = ctype.includes("application/json") ? JSON.stringify(await res.json()) : await res.text();
        return { success: false, message: `HTTP ${res.status}: ${txt?.slice?.(0, 180)}` };
      }

      const json: any = ctype.includes("application/json") ? await res.json() : {};
      const data = json?.data && !Array.isArray(json.data) ? json.data : json;
      const numero = data?.numero ?? data?.nro ?? data?.codigo ?? data?.id;
      const msg = data?.message || json?.message || "Pedido generado correctamente";

      this.dispatchCartEvent("api-send-order");
      return { success: true, data: { ...data, numero }, message: msg };
    } catch (error) {
      return { success: false, message: this.getErrorMessage(error) };
    }
  }

  async enviarPedido(payload: EnviarPedidoRequest): Promise<ApiResponse<EnviarPedidoResponse>> {
    try {
      const idtipo_pedido = this.mapTipoEnvioATipoPedido(payload.tipo_envio);
      const items = payload.items.map((it) => ({ cod_drog: String(it.cod_drog), cantidad: Number(it.cantidad) || 1 }));
      return await this.enviarPedidoV1({ idtipo_pedido, items });
    } catch (error) {
      try {
        const resp = await this.postWithFallback("pedidos/enviar", payload);
        if (resp?.success) {
          this.dispatchCartEvent("api-send-order");
          return { success: true, data: resp.data, message: resp.message || "Pedido enviado" };
        }
        return { success: false, message: resp?.message || "No se pudo enviar el pedido" };
      } catch (e) {
        return { success: false, message: this.getErrorMessage(e) };
      }
    }
  }

  // ----- Historial -----
  async obtenerHistorialCarritos(limite: number = 10): Promise<ApiResponse<any[]>> {
    try {
      const resp = await this.getWithFallback(`carrito/historial?limite=${limite}`);
      if (resp?.code === 404 || resp?.code === 405) {
        return { success: false, message: "Funcionalidad no disponible en el servidor actual" };
      }
      return { success: Boolean(resp?.success), data: resp?.data || [], message: resp?.message };
    } catch (e) {
      return { success: false, message: this.getErrorMessage(e) };
    }
  }

  async restaurarCarrito(carritoId: string | number): Promise<ApiResponse> {
    try {
      const resp = await this.postWithFallback(`carrito/restaurar/${carritoId}`, {});
      if (resp?.code === 404 || resp?.code === 405) {
        return { success: false, message: "Funcionalidad no disponible en el servidor actual" };
      }
      this.dispatchCartEvent("api-restore-cart");
      return { success: Boolean(resp?.success), data: resp?.data, message: resp?.message || "Carrito restaurado" };
    } catch (e) {
      return { success: false, message: this.getErrorMessage(e) };
    }
  }

  /* ========================
   * Utilidades privadas
   * ====================== */
  private toInt(v: any, def = 0): number {
    const n = parseInt(String(v), 10);
    return Number.isFinite(n) ? n : def;
  }

  private preferirPrecioOferta(raw: any, fallback = 0): number {
    const candidatos = [
      toNum(raw?.precio_oferta),
      toNum(raw?.precio_si),
      toNum(raw?.pr_farmacia_si),
      toNum(raw?.pr_farmacia_con_descuento),
      toNum(raw?.precio),
      toNum(raw?.precio_unitario),
      toNum(fallback),
    ].filter((n) => Number.isFinite(n) && n > 0);
    return candidatos.length ? Math.min(...candidatos) : 0;
  }

  private esComboOferta(combo: any): boolean {
    const tipo = String(combo?.tipo_combo || "").toLowerCase();
    const ts = String(combo?.tipo_solicitante || "").toUpperCase();
    // Solo basarse en tipo_solicitante y tipo_combo — NO en combo_info (es nombre libre)
    return ts === "O" || tipo === "oferta_online" || tipo === "oferta";
  }

  private esOfertaRaw(p: any): boolean {
    const ts = String(p?.tipo_solicitante || "").toUpperCase();
    if (ts === "O") return true;

    if (p?.promocional === true) return true;

    const desc = toNum(p?.desc_porc ?? p?.descuento ?? 0);
    if (desc > 0) return true;

    return false; // ✅ NO confiar en precio_oferta o precio_si
  }

  private procesarRespuestaCarrito(response: any): {
    lineasCarrito: ProductoCarritoAPI[];
    ofertasOnline: ProductoCarritoAPI[];
  } {
    const lineasCarrito: ProductoCarritoAPI[] = [];
    const ofertasOnline: ProductoCarritoAPI[] = [];

    const getFechaCarga = (combo: any): string => {
      const p0 = Array.isArray(combo?.productos) && combo.productos.length > 0 ? combo.productos[0] : null;
      return String(combo?.fecha_carga || p0?.fecha_carga || "");
    };

    const getDescPct = (combo: any): number => {
      if (combo?.desc_porc != null) return toNum(combo.desc_porc);
      const p0 = Array.isArray(combo?.productos) && combo.productos.length > 0 ? combo.productos[0] : null;
      return toNum(p0?.desc_porc);
    };

    const totalComboSinIVA = (combo: any): number => {
      const productos = Array.isArray(combo?.productos) ? combo.productos : [];
      const sumaSinIVA = productos.reduce((acc: number, row: any) => {
        const cant = Math.max(1, this.toInt(row?.cantidad, 1));
        let unit = toNum(row?.precio_si);
        if (unit <= 0) unit = Math.max(toNum(row?.precio_oferta), toNum(row?.pr_farmacia_si));
        if (unit <= 0) {
          const conIva = toNum(row?.precio) || toNum(row?.total);
          unit = conIva > 0 ? conIva / 1.21 : 0;
        }
        return acc + unit * cant;
      }, 0);

      // Prioridad 1: monto_total_sin_iva (campo directo del API, sin IVA)
      const mtSinIVA = toNum(combo?.monto_total_sin_iva);
      if (mtSinIVA > 0) return r2(mtSinIVA);

      // Prioridad 2: suma de precio_si × cantidad
      if (sumaSinIVA > 0) return r2(sumaSinIVA);

      // Fallback: monto_total normalizado
      const mt = toNum(combo?.monto_total);
      if (mt > 0) return r2(mt / 1.21);
      return 0;
    };

    const buildComboItem = (combo: any, esOferta: boolean): ProductoCarritoAPI => {
      const getCantidadCombos = (combo: any): number => {
        const direct = this.toInt(combo?.cantidad ?? combo?.cant, 0);
        const viaCombos = this.toInt(combo?.cantidad_combos, 0);
        let c = direct || viaCombos || 1;

        if (c > 5 && Array.isArray(combo?.productos)) {
          const sumaInterna = combo.productos.reduce((s: number, r: any) => s + this.toInt(r?.cantidad, 1), 0);
          if (sumaInterna > 0 && c === sumaInterna) c = 1;
        }
        return Math.max(1, c);
      };

      const cantidadOferta = Array.isArray(combo?.productos)
        ? combo.productos.reduce((s: number, r: any) => s + this.toInt(r?.cantidad, 1), 0)
        : 1;

      // ✅ CRITICAL: Combos con tipo_solicitante "O"/"D" SIEMPRE tienen cantidad 1 (precio fijo)
      const tipoSolicitanteCheck = String(combo?.tipo_solicitante || "").toUpperCase();
      const esComboPrecioFijo = tipoSolicitanteCheck === "O" || tipoSolicitanteCheck === "D";
      const cant = esComboPrecioFijo ? 1 : (esOferta ? Math.max(1, cantidadOferta) : getCantidadCombos(combo));

      const totalSinIVA = totalComboSinIVA(combo);
      const fecha_carga = getFechaCarga(combo);
      const desc_porc = getDescPct(combo);
      const totalConDto = esOferta && desc_porc > 0 ? r2(totalSinIVA * (1 - desc_porc / 100)) : totalSinIVA;

      // ✅ Auditoría de carga para combos
      const p0_audit = Array.isArray(combo?.productos) && combo.productos.length > 0 ? combo.productos[0] : null;
      const usuario_carga = combo?.usuario_carga || p0_audit?.usuario_carga;
      const nombre_carga = combo?.nombre_carga || p0_audit?.nombre_carga;

      // ✅ CRITICAL: Preserve original tipo_solicitante for combos from pedidos_combos_oferta
      // If esOferta=true and the combo already has tipo_solicitante, preserve it (it's a C/L combo)
      // Otherwise, default to "C" for combos in "Mis Pedidos Ofertas Online"
      const tipoSolicitanteOriginal = String(combo?.tipo_solicitante || "").toUpperCase();
      const tipoFinal = esOferta && (tipoSolicitanteOriginal === "C" || tipoSolicitanteOriginal === "L")
        ? tipoSolicitanteOriginal
        : (esOferta ? "C" : combo?.tipo_solicitante || "C");

      return {
        id_pedido: combo?.id,
        id: combo?.id,
        cod_drog: combo?.cod_combo?.toString() || (esOferta ? `COMBO_OFERTA_${combo?.id}` : `COMBO_${combo?.id}`),
        descripcion: `${esOferta ? "COMBO OFERTA" : "COMBO"}: ${combo?.combo_info || "Especial"}`,
        cantidad: cant,
        precio_unitario: r2(totalConDto / Math.max(1, cant)),
        precio_total: totalConDto,
        tipo_solicitante: tipoFinal as any,
        es_combo: true,
        combo: esOferta ? "COMBO OFERTA" : "COMBO NORMAL",
        combo_id: combo?.id,
        observacion: `Combo ID: ${combo?.id} | Productos: ${combo?.productos?.length || 0}`,
        descuento: (Array.isArray(combo?.productos) ? combo.productos : []).reduce(
          (s: number, p: any) => s + toNum(p?.desc_porc),
          0
        ),
        stock: "S",
        is_oferta_online: esOferta,
        scope: esOferta ? ("mis-pedidos" as const) : ("carrito" as const),
        raw_combo: combo,
        monto_total: totalConDto,
        fecha_carga,
        desc_porc,
        // Auditoría
        usuario_carga: usuario_carga ? String(usuario_carga) : undefined,
        nombre_carga: nombre_carga ? String(nombre_carga) : undefined,
      } as any;
    };

    // ✅ ========== SECCIÓN CORREGIDA: pedidos_individuales ==========
    if (Array.isArray(response?.data?.pedidos_individuales)) {
      const arr = response.data.pedidos_individuales.map((p: any) => {
        const cant = Math.max(1, this.toInt(p.cantidad, 1));

        // ✅ DETECCIÓN ESTRICTA DE OFERTA
        const ts = String(p?.tipo_solicitante || "").toUpperCase();
        const descPorc = toNum(p?.desc_porc ?? p?.descuento ?? 0);
        const promocional = p?.promocional === true;

        // Solo es oferta si cumple ALGUNO de estos criterios:
        const esOferta = ts === "O" || descPorc > 0 || promocional;

        // ✅ CORREGIDO: Usar campos correctos del API
        // El API proporciona:
        // - precio: unitario CON descuento (lo que va en "Precio c/Dto s/IVA")
        // - precio_unitario_sin_iva: unitario SIN descuento, SIN IVA (lo que va en "Precio s/IVA")
        // - importe_sin_iva: total SIN IVA
        const precioDelApi = toNum(p.precio ?? 0); // CON descuento (para mostrar en columna)
        const precio_unitario_conIva = toNum(p.precio ?? p.precio_unitario ?? 0);
        const precioSiDelApi = toNum(p.precio_si ?? 0); // SIN IVA pero puede tener descuento
        const precioUnitarioSinIvaDelApi = toNum(p.precio_unitario_sin_iva ?? 0); // SIN descuento, SIN IVA
        const importeSinIva = toNum(p.importe_sin_iva ?? 0);
        const importeConIva = toNum(p.importe_con_iva ?? 0);

        // Usar precio CON descuento para el carrito (precio_si del API)
        const precio_unitario = precioSiDelApi > 0 ? r2(precioSiDelApi) : r2(precio_unitario_conIva);

        // ✅ Usar importe del API si existe, sino calcular
        const precio_total = importeSinIva > 0 ? r2(importeSinIva) : r2(precio_unitario * cant);

        // ✅ Precio lista: SOLO calcular si ES oferta
        let precio_lista = 0;
        const EPS = 0.01;

        if (esOferta && precio_unitario_conIva > precio_unitario + EPS) {
          // Si hay oferta, el precio_unitario_conIva es el precio tachado
          precio_lista = r2(precio_unitario_conIva);
        }

        // ✅ MAPEO CORRECTO DE PRECIOS DEL API:
        // El API proporciona campos que DEBEN preservarse tal cual:
        // - precio_unitario_sin_iva: precio unitario SIN descuento, SIN IVA (para "Precio s/IVA")
        // - precio_si: precio unitario CON descuento, SIN IVA (para "Precio c/Dto s/IVA")
        // - importe_sin_iva: importe total SIN IVA (para "Importe")

        const item: ProductoCarritoAPI = {
          id_pedido: p.id_pedido,
          id: p.id_pedido,
          cod_drog: p.cod_drog,
          descripcion: p.descripcion,
          cantidad: cant,
          precio_unitario,
          precio_total,

          // ✅ tipo_solicitante basado en detección
          tipo_solicitante: p.tipo_solicitante || (esOferta ? "O" : "C"),

          es_combo: false,
          combo: false,
          observacion: p.observacion,
          descuento: descPorc,
          stock: p.stock ?? "S",
          cod_barra: p.cod_barra,
          rubro: p.rubro,
          laboratorio: p.laboratorio,
          marca: p.marca,
          expendedor: p.expendedor,
          oferta: p.oferta,
          refrigerado: p.refrigerado,
          transfer: p.transfer,
          fora: p.fora,
          dto_general: p.dto_general,
          iva: p.iva,
          unidxbulto: p.unidxbulto,
          idimagen: p.idimagen,
          scope: "carrito" as const,

          // ✅ CORREGIDO: SIEMPRE crear estos campos (NUNCA null/undefined)
          // Preservar directamente del API tal cual vienen
          // 🔴 IMPORTANTE: Estos campos son CRÍTICOS para cart-page.ts:
          precio: r2(precioDelApi), // CON descuento (para "Precio c/Dto s/IVA")
          precio_si: r2(precioSiDelApi), // SIN IVA (puede tener descuento)
          precio_unitario_sin_iva: r2(precioUnitarioSinIvaDelApi), // SIN descuento, SIN IVA (para "Precio s/IVA")
          importe_sin_iva: r2(importeSinIva), // Total SIN IVA (del API)
          importe_con_iva: r2(importeConIva), // Total CON IVA (del API)
          precio_oferta: esOferta ? r2(precioDelApi) : 0, // Precio de oferta (CON descuento)
          precio_lista: r2(precio_lista), // Precio tachado (con IVA si es oferta)

          // Otros campos
          pr_farmacia_si: toNum(p.pr_farmacia_si),
          pr_farmacia_con_descuento: toNum(p.pr_farmacia_con_descuento),
          pr_publico: toNum(p.pr_publico),
          desc_porc: r2(descPorc), // SIEMPRE
          is_oferta_online: Boolean(p.is_oferta_online),

          // Campos opcionales
          ...(p?.fecha_carga ? { fecha_carga: String(p.fecha_carga) } : {}),
          usuario_carga: p?.usuario_carga ? String(p.usuario_carga) : undefined,
          nombre_carga: p?.nombre_carga ? String(p.nombre_carga) : undefined,
          ...(p?.cant_revista != null ? { cant_revista: Number(p.cant_revista) } : {}),
          // ✅ PRESERVAR unidades_por_modulo del API para validación de cantidad en carrito
          ...(p?.unidades_por_modulo != null ? { unidades_por_modulo: Number(p.unidades_por_modulo) } : {}),
          // ✅ PRESERVAR cantmin_venta del API para validación de cantidad mínima en carrito
          ...(p?.cantmin_venta != null ? { cantmin_venta: Number(p.cantmin_venta) } : {}),
        };

        return item;
      });

      lineasCarrito.push(...arr);
    }
    // ✅ ========== FIN SECCIÓN CORREGIDA ==========

    // ✅ Función para generar key única de combo
    const getComboKey = (combo: any): string => {
      // Prioridad 1: id del combo (más confiable)
      const comboId = String(combo?.id ?? "").trim();
      if (comboId && comboId !== "0" && comboId !== "null" && comboId !== "undefined") {
        return `COMBO_ID:${comboId}`;
      }

      // Prioridad 2: cod_combo
      const codCombo = String(combo?.cod_combo ?? combo?.codigo_combo ?? "").trim();
      if (codCombo && codCombo !== "0" && codCombo !== "null" && codCombo !== "undefined") {
        return `COD_COMBO:${codCombo}`;
      }

      // Fallback: combo_info + descuento
      const info = String(combo?.combo_info ?? combo?.descripcion ?? "").toLowerCase().trim();
      const pct = getDescPct(combo);
      return `DESC:${info}::${pct}`;
    };

    // ✅ Sets para trackear combos ya procesados y evitar duplicados
    const combosProcesados = new Set<string>();
    const combosOfertaProcesados = new Set<string>();

    // Combos normales
    if (Array.isArray(response?.data?.pedidos_combos)) {
      response.data.pedidos_combos.forEach((combo: any) => {
        const key = getComboKey(combo);

        // ✅ Evitar duplicados: si ya está en combos oferta, saltarlo
        if (combosOfertaProcesados.has(key)) {
          console.log(`🔍 [CART-API] Combo ya procesado como oferta (skip):`, { key, combo_info: combo?.combo_info });
          return;
        }

        // ✅ Evitar duplicados dentro del mismo array
        if (combosProcesados.has(key)) {
          console.log(`🔍 [CART-API] Combo duplicado detectado (skip):`, { key, combo_info: combo?.combo_info });
          return;
        }

        const esOferta = !!this.esComboOferta(combo);
        const item = buildComboItem(combo, esOferta);

        combosProcesados.add(key);

        if (item.is_oferta_online) {
          combosOfertaProcesados.add(key);
          ofertasOnline.push(item);
        } else {
          lineasCarrito.push(item);
        }
      });
    }

    // Combos oferta
    if (Array.isArray(response?.data?.pedidos_combos_oferta)) {
      response.data.pedidos_combos_oferta.forEach((combo: any) => {
        const key = getComboKey(combo);

        // ✅ Evitar duplicados: si ya fue procesado, saltarlo
        if (combosOfertaProcesados.has(key) || combosProcesados.has(key)) {
          console.log(`🔍 [CART-API] Combo oferta duplicado detectado (skip):`, { key, combo_info: combo?.combo_info });
          return;
        }

        // ✅ CRITICAL: Si el combo tiene tipo_solicitante "O" o "D", va al carrito normal (Mi Carrito)
        const tipoSolicitante = String(combo?.tipo_solicitante || "").toUpperCase();
        const esOfertaOnlineDirecta = tipoSolicitante === "O" || tipoSolicitante === "D";

        if (esOfertaOnlineDirecta) {
          // Combo tipo O/D va a Mi Carrito
          console.log(`✅ [CART-API] Combo tipo ${tipoSolicitante} va a Mi Carrito:`, { key, combo_info: combo?.combo_info });
          const item = buildComboItem(combo, false);
          combosProcesados.add(key);
          lineasCarrito.push(item);
        } else {
          // Combos tipo C/L van a Mis Pedidos Ofertas Online
          console.log(`📋 [CART-API] Combo tipo ${tipoSolicitante || 'C'} va a Mis Pedidos Ofertas Online:`, { key, combo_info: combo?.combo_info });
          const item = buildComboItem(combo, true);
          combosOfertaProcesados.add(key);
          ofertasOnline.push(item);
        }
      });
    }

    return { lineasCarrito, ofertasOnline };
  }

  private calcularSubtotal(arr: ProductoCarritoAPI[]): number {
    return r2(arr.reduce((sum, p) => sum + (Number(p.precio_total) || 0), 0));
  }
  private calcularDescuentos(arr: ProductoCarritoAPI[]): number {
    return r2(
      arr.reduce((sum, p) => {
        const d = Number(p.descuento) || 0;
        const c = Number(p.cantidad) || 1;
        return sum + d * c;
      }, 0)
    );
  }
  private calcularCantidadItems(arr: ProductoCarritoAPI[]): number {
    return arr.reduce((sum, p) => sum + (Number(p.cantidad) || 0), 0);
  }

  private esCategoria(
    producto: ProductoCarritoAPI,
    categoria: "medicamentos" | "perfumeria" | "accesorios"
  ): boolean {
    const descripcion = (producto.descripcion || "").toLowerCase();
    const rubro = (producto.rubro || "").toLowerCase();
    switch (categoria) {
      case "medicamentos":
        return (
          rubro === "m" ||
          rubro === "medicamentos" ||
          descripcion.includes("comp") ||
          descripcion.includes("caps") ||
          descripcion.includes("tab") ||
          descripcion.includes("jarabe") ||
          descripcion.includes("susp") ||
          descripcion.includes("mg") ||
          descripcion.includes("ml") ||
          descripcion.includes("gotas") ||
          descripcion.includes("ampolla") ||
          descripcion.includes("inyectable")
        );
      case "perfumeria":
        return (
          rubro === "p" ||
          rubro === "perfumeria" ||
          descripcion.includes("crema") ||
          descripcion.includes("locion") ||
          descripcion.includes("shampoo") ||
          descripcion.includes("gel") ||
          descripcion.includes("perfume") ||
          descripcion.includes("desodorante") ||
          descripcion.includes("colonia") ||
          descripcion.includes("fragancia")
        );
      case "accesorios":
        return (
          rubro === "c" ||
          rubro === "a" ||
          rubro === "accesorios" ||
          rubro === "algodones" ||
          descripcion.includes("termometro") ||
          descripcion.includes("jeringa") ||
          descripcion.includes("venda") ||
          descripcion.includes("gasa") ||
          descripcion.includes("alcohol") ||
          descripcion.includes("algodon") ||
          descripcion.includes("pañal") ||
          descripcion.includes("mascarilla")
        );
      default:
        return false;
    }
  }

  private getErrorMessage(error: unknown): string {
    if (typeof error === "string") return error;
    if (error instanceof Error) return error.message;
    const e: any = error;
    if (e?.response?.data?.message) return e.response.data.message;
    if (e?.response?.data?.error) return e.response.data.error;
    if (e?.message) return e.message;
    if (e?.data?.message) return e.data.message;
    if (e?.error) return e.error;
    if (e?.code === "NETWORK_ERROR") return "Error de conexión. Verifica tu conexión a internet.";
    if (e?.code === "TIMEOUT_ERROR") return "Tiempo de espera agotado. Inténtalo de nuevo.";
    if (e?.status === 404) return "Recurso no encontrado en el servidor.";
    if (e?.status === 401) return "No tienes permisos para realizar esta acción.";
    if (e?.status >= 500) return "Error interno del servidor. Inténtalo más tarde.";
    return "Error desconocido en la comunicación con el servidor";
  }

  /* ===== Compat helpers (no tocar otras capas) ===== */
  convertirAProductoLocal(
    productoAPI: ProductoCarritoAPI
  ): import("./cart-service-hybrid.js").ProductoCarrito {
    return {
      id: productoAPI.id_pedido || productoAPI.id || `local_${Date.now()}`,
      cod_drog: productoAPI.cod_drog,
      descripcion: productoAPI.descripcion,
      precio_unitario: productoAPI.precio_unitario,
      cantidad: productoAPI.cantidad,
      precio_total: productoAPI.precio_total,
      imagen: productoAPI.imagen,
      descuento: productoAPI.descuento,
      tipo_solicitante: productoAPI.tipo_solicitante,
      es_combo: productoAPI.es_combo,
      combo_id: productoAPI.combo_id,
      combo: productoAPI.combo,
      observacion: productoAPI.observacion,
      rubro: productoAPI.rubro,
      laboratorio: productoAPI.laboratorio,
      marca: productoAPI.marca,
      expendedor: productoAPI.expendedor,
      oferta: productoAPI.oferta,
      refrigerado: productoAPI.refrigerado,
      transfer: productoAPI.transfer,
      fora: productoAPI.fora,
      dto_general: productoAPI.dto_general,
      iva: productoAPI.iva,
      stock: productoAPI.stock,
      unidxbulto: productoAPI.unidxbulto,
      idimagen: productoAPI.idimagen,
      cod_barra: productoAPI.cod_barra,
      usuario_carga: productoAPI.usuario_carga,
      nombre_carga: productoAPI.nombre_carga,
    };
  }
  convertirAAgregarRequest(
    productoLocal: import("./cart-service-hybrid.js").ProductoCarrito
  ): AgregarProductoRequest {
    return {
      cod_drog: productoLocal.cod_drog,
      cantidad: String(productoLocal.cantidad),
      tipo_solicitante:
        (productoLocal.tipo_solicitante as "C" | "V" | "O" | "T" | "P") || "C",
      observacion: productoLocal.observacion || "",
      forzar_sin_combo: false,
    };
  }

  /* ===== Métricas/diagnóstico ===== */
  /* Métricas removidas (throttle desactivado) */
  async forzarSincronizacion(): Promise<CarritoResponse> {
    // limpiarThrottleCache removido
    return this.obtenerCarrito();
  }
  async verificarConexion(): Promise<boolean> {
    try {
      await this.getWithFallback("carrito");
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Verifica si un producto ya está en el carrito
   * GET /api/v1/carrito/verificar/{cod_drog}
   */
  async verificarProductoEnCarrito(codDrog: string): Promise<{ existe: boolean; cantidad?: number; data?: any }> {
    try {
      const response = await this.getV1<any>(`carrito/verificar/${codDrog}`);

      // La API devuelve { en_carrito: true/false, cantidad: N }
      const enCarrito = response?.en_carrito ?? response?.data?.en_carrito ?? false;
      const cantidad = response?.cantidad ?? response?.data?.cantidad ?? 0;

      return {
        existe: enCarrito,
        cantidad,
        data: response
      };
    } catch (error) {
      console.warn(`⚠️ [verificarProductoEnCarrito] Error verificando ${codDrog}:`, error);
      return { existe: false };
    }
  }

  // Ofertas online (v1 separado, sólo para listar combos de oferta agregados por otra vista)
  async obtenerCarritoOfertasOnline(): Promise<CarritoResponse> {
    try {
      const response = await this.getV1<any>("ofertas-online/carrito");

      const rawOfertas = Array.isArray(response?.data) ? response.data : [];
      if (!rawOfertas.length) {
        return {
          success: true,
          data: {
            productos: [], medicamentos: [], perfumeria: [], accesorios: [], otros: [],
            subtotal: 0, descuentos: 0, total: 0, cantidad_items: 0,
            ofertas_online: [], total_ofertas_online: 0, cantidad_ofertas_online: 0,
            pedidos_individuales: [], pedidos_combos: [], pedidos_combos_oferta: [],
          },
        };
      }

      // ✅ Función para generar key única de oferta
      const getOfertaKey = (item: any, index: number): string => {
        // Prioridad 1: id del combo
        const comboId = String(item?.id ?? "").trim();
        if (comboId && comboId !== "0" && comboId !== "null" && comboId !== "undefined") {
          return `COMBO_ID:${comboId}`;
        }

        // Prioridad 2: cod_combo
        const codCombo = String(item?.cod_combo ?? item?.codigo_combo ?? "").trim();
        if (codCombo && codCombo !== "0" && codCombo !== "null" && codCombo !== "undefined") {
          return `COD_COMBO:${codCombo}`;
        }

        // Fallback: descripción + descuento + fecha
        const desc = String(item?.descripcion ?? item?.combo_info ?? "Oferta Online").toLowerCase().trim();
        const pct = toNum(item.dto_mostrar || item.desc_porc || 0);
        const fecha = String(item?.fecha ?? "").trim();
        return `DESC:${desc}::${pct}::${fecha}`;
      };

      // ✅ Eliminar duplicados usando keys
      const keysVistas = new Set<string>();
      const ofertasUnicas: ProductoCarritoAPI[] = [];

      // ✅ Priorizar items que tengan nombre_carga para que el deduplicador los elija
      const sortedOfertas = [...rawOfertas].sort((a, b) => {
        const hasA = (a.nombre_carga || a.usuario_nombre) ? 1 : 0;
        const hasB = (b.nombre_carga || b.usuario_nombre) ? 1 : 0;
        return hasB - hasA;
      });

      sortedOfertas.forEach((item: any, index: number) => {
        const key = getOfertaKey(item, index);

        // ✅ Evitar duplicados
        if (keysVistas.has(key)) {
          console.log(`🔍 [obtenerCarritoOfertasOnline] Oferta duplicada detectada (skip):`, { key, descripcion: item?.descripcion || item?.combo_info });
          return;
        }

        keysVistas.add(key);

        const cantidad = Math.max(1, this.toInt(item.cantidad, 1));
        const desc_porc = toNum(item.dto_mostrar || item.desc_porc || 0);
        const combo_id = String(item?.id || item?.cod_combo || `OFERTA_${item.fecha}_${index}`);
        let descripcion = String(item?.descripcion || item?.combo_info || "Oferta Online").trim();
        if (!descripcion.includes("")) descripcion = ` ${descripcion}`;

        // Calcular precio con descuento desde los campos del backend
        const montoRaw = toNum(item.monto_total || item.precio_total || 0);
        const montoConDto = montoRaw > 0 && desc_porc > 0
          ? r2(montoRaw * (1 - desc_porc / 100))
          : montoRaw;
        const precioUnit = cantidad > 0 ? r2(montoConDto / cantidad) : montoConDto;

        ofertasUnicas.push({
          id_pedido: combo_id, id: combo_id, cod_drog: combo_id, combo_id,
          descripcion, cantidad, precio_unitario: precioUnit, precio_total: montoConDto,
          monto_total: montoConDto,
          tipo_solicitante: "C", es_combo: true, combo: "COMBO OFERTA",
          observacion: `Oferta online - ${desc_porc}% OFF`, descuento: desc_porc,
          stock: "S", is_oferta_online: true, scope: "mis-pedidos" as const,
          fecha_carga: item.fecha || "", desc_porc, raw_combo: item,
          usuario_carga: item.nombre_carga ? (item.usuario_carga || item.id_usuario || "") : "",
          nombre_carga: item.nombre_carga || item.usuario_nombre || "",
        } as ProductoCarritoAPI);
      });

      return {
        success: true,
        data: {
          productos: [], medicamentos: [], perfumeria: [], accesorios: [], otros: [],
          subtotal: 0, descuentos: 0, total: 0, cantidad_items: 0,
          ofertas_online: ofertasUnicas, total_ofertas_online: 0,
          cantidad_ofertas_online: ofertasUnicas.length,
          pedidos_individuales: [], pedidos_combos: [],
          pedidos_combos_oferta: ofertasUnicas.map((o) => o.raw_combo),
        },
      };
    } catch (error) {
      console.error("❌ [obtenerCarritoOfertasOnline] Error:", error);
      return {
        success: false,
        data: {
          productos: [], subtotal: 0, descuentos: 0, total: 0, cantidad_items: 0,
          ofertas_online: [], total_ofertas_online: 0, cantidad_ofertas_online: 0,
          pedidos_individuales: [], pedidos_combos: [], pedidos_combos_oferta: [],
        },
        message: this.getErrorMessage(error),
      };
    }
  }

  async debug(): Promise<void> {
    if (process.env.NODE_ENV !== "development") return;
    try {
      console.group("🐛 Cart API Service Debug");
      const ok = await this.verificarConexion();
      if (ok) {
        const carrito = await this.obtenerCarrito();
        console.debug("Carrito:", carrito?.data);
      }
      console.groupEnd();
    } catch (e) {
      console.error("❌ Error en debug:", e);
    }
  }
}

/* ============ Export singleton + helpers dev ============ */
export const cartApiService = new CartApiService();

if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
  (window as any).cartApiDebug = () => cartApiService.debug();
  (window as any).cartApiForceSync = () => cartApiService.forzarSincronizacion();
}